import React, { useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AOS from "aos";
import {
  ShieldCheck,
  User,
  Database,
  Globe,
  Lock,
  Mail,
  Cookie,
  FileText,
  Scale,
  RefreshCcw,
  Server,
} from "lucide-react";

const Privacy = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      easing: "ease-in-out",
    });
  }, []);

  const ListItem = ({ icon: Icon, children, className = "" }) => (
    <li className="flex items-start gap-3 text-gray-700 leading-relaxed">
      <Icon className="w-5 h-5 text-orange-500 mt-1 shrink-0" />
      <span className={`text-gray-700 ${className}`}>{children}</span>
    </li>
  );

  return (
    <>
      <Header />

      <section className="bg-gradient-to-br from-orange-50 via-white to-orange-100 py-20">
        <div className="max-w-5xl mx-auto px-6">
          {/* Hero Section */}
          <div className="text-center mb-16" data-aos="fade-down">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              Privacy Policy
            </h1>
            <p className="text-gray-600 text-lg">
              Last updated: January 9, 2025
            </p>
          </div>

          {/* Glass Card */}
          <div className=" space-y-12">
            <div
              className="space-y-4 text-gray-700 space-y-4"
              data-aos="fade-left"
            >
              <p className="font-semibold text-3xl lg:text-4xl text-gray-900">
                Privacy Policy for TiffinTap App
              </p>
              <p>Effective Date: 01 Sep 2025</p>
              <p>Last Updated: 15 Sep 2025</p>
              <p className="leading-8">
                This Privacy Policy explains how TiffinTap ("we," "our," or
                "us") collects, uses, and protects your personal information
                when you use our website (tiffintap.com) and mobile/web
                applications (the "Services").
              </p>
              <p>
                By accessing or using our Services, you agree to this Privacy
                Policy.
              </p>
            </div>

            {/* 1 */}
            <div data-aos="fade-up">
              <h2 className="text-3xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                <Database className="text-orange-500" /> 1. Information We
                Collect
              </h2>

              <h3 className="font-semibold text-lg mb-3">
                1.1 Personal Information You Provide
              </h3>
              <ul className="space-y-3 mb-6">
                <ListItem icon={User}>
                  Account Details: Name, phone number, email address, profile
                  photo, and login credentials.
                </ListItem>
                <ListItem icon={FileText}>
                  Service Details (for Providers): Tiffin service name,
                  description, cuisine information, pricing, addresses, delivery
                  preferences, payment collection.
                </ListItem>
                <ListItem icon={ShieldCheck}>
                  Order Details (for Customers): Order history, chosen cuisine,
                  pickup/delivery details, payment notes.
                </ListItem>
              </ul>

              <h3 className="font-semibold text-lg mb-3">
                1.2 Automatically Collected Information
              </h3>
              <ul className="space-y-3 mb-6">
                <ListItem icon={Server}>
                  Device and browser type, IP address, operating system, time
                  zone.
                </ListItem>
                <ListItem icon={Globe}>
                  Usage activity such as pages visited, clicks, search terms.
                </ListItem>
                <ListItem icon={Cookie}>
                  Cookies and tracking technologies (see Section 6).
                </ListItem>
              </ul>

              <h3 className="font-semibold text-lg mb-3">
                1.3 Communication Data
              </h3>
              <ul className="space-y-3">
                <ListItem icon={Mail}>
                  Records of communication via email, WhatsApp, or SMS
                  notifications.
                </ListItem>
              </ul>
            </div>

            {/* 2 */}
            <div data-aos="fade-up">
              <h2 className="text-3xl font-semibold text-gray-900 mb-6">
                2. How We Use Your Information
              </h2>
              <ul className="space-y-3">
                <ListItem icon={ShieldCheck}>
                  Provide and improve our Services.
                </ListItem>
                <ListItem icon={User}>
                  Allow providers to manage orders, menus, and payments.
                </ListItem>
                <ListItem icon={FileText}>
                  Allow customers to view menus, place orders, and track
                  history.
                </ListItem>
                <ListItem icon={Mail}>
                  Send service-related communications.
                </ListItem>
                <ListItem icon={Database}>
                  Analyse usage and improve performance.
                </ListItem>
                <ListItem icon={Scale}>Comply with legal obligations.</ListItem>
              </ul>
            </div>

            {/* 3 */}
            <div data-aos="fade-up">
              <h2 className="text-3xl font-semibold text-gray-900 mb-6">
                3. Sharing Your Information
              </h2>
              <ul className="space-y-3">
                <ListItem icon={Server}>
                  Service Providers: Hosting, payment gateways, analytics,
                  notification services.
                </ListItem>
                <ListItem icon={Scale}>
                  Legal Authorities: To comply with laws and regulations.
                </ListItem>
                <ListItem icon={RefreshCcw}>
                  Business Transfers: In merger or acquisition events.
                </ListItem>
              </ul>
            </div>

            {/* 4 */}
            <div data-aos="fade-up">
              <h2 className="text-3xl font-semibold text-gray-900 mb-4">
                4. International Data Transfers
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Your information may be stored on servers outside your country
                (including AWS India). By using our Services, you consent to
                such transfers, subject to safeguards under GDPR, CCPA, PIPEDA,
                and other regional laws.
              </p>
            </div>

            {/* 5 */}
            <div data-aos="fade-up">
              <h2 className="text-3xl font-semibold text-gray-900 mb-6">
                5. Your Rights
              </h2>
              <ul className="space-y-3">
                <ListItem icon={Scale}>GDPR (EU/UK) Rights.</ListItem>
                <ListItem icon={Scale}>CCPA (California) Rights.</ListItem>
                <ListItem icon={Scale}>PIPEDA (Canada) Rights.</ListItem>
                <ListItem icon={Scale}>Australia Privacy Act Rights.</ListItem>
              </ul>
              <p className="mt-4">
                To exercise rights, email us at{" "}
                <span className="text-orange-500 font-medium">
                  privacy@tiffintap.com
                </span>
              </p>
            </div>

            {/* 6 */}
            <div data-aos="fade-up">
              <h2 className="text-3xl font-semibold text-gray-900 mb-6">
                6. Cookies & Analytics
              </h2>
              <ul className="space-y-3">
                <ListItem icon={Cookie}>
                  Cookies for login & preferences.
                </ListItem>
                <ListItem icon={Globe}>Google Analytics usage.</ListItem>
                <ListItem icon={Database}>Future advertising cookies.</ListItem>
              </ul>
            </div>

            {/* 7-11 */}
            <div className="space-y-8" data-aos="fade-up">
              <div>
                <h2 className="text-3xl font-semibold mb-2">
                  7. Children's Privacy
                </h2>
                <p>
                  Our Services are not directed to children under 13 years old.
                  We do not knowingly collect information from children.
                </p>
              </div>

              <div>
                <h2 className="text-3xl font-semibold mb-2">
                  8. Retention of Data
                </h2>
                <p>
                  We retain your data as long as your account is active or as
                  required by law.
                </p>
              </div>

              <div>
                <h2 className="text-3xl font-semibold mb-2">9. Security</h2>
                <p>
                  We use encryption, access controls, and secure hosting to
                  protect your data.
                </p>
              </div>

              <div>
                <h2 className="text-3xl font-semibold mb-2">10. Changes</h2>
                <p>
                  We may update this Privacy Policy periodically. Changes will
                  be effective when posted.
                </p>
              </div>

              <div>
                <h2 className="text-3xl font-semibold mb-4">11. Contact Us</h2>
                <ul className="space-y-2">
                  <ListItem icon={Mail}>
                    <a
                      href="mailto:hello@webapprise.com"
                      className="text-orange-500 hover:underline transition-all ease-in-out duration-500"
                    >
                      hello@webapprise.com
                    </a>
                  </ListItem>

                  <ListItem icon={Globe}>
                    <a
                      href="https://www.tiffintap.com/"
                      target="_blank"
                      className="text-orange-500 hover:underline transition-all ease-in-out duration-500 cursor-pointer"
                    >
                      www.tiffintap.com
                    </a>
                  </ListItem>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Privacy;
